import { useEffect, useRef } from 'react';
import { format, parse } from 'date-fns';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useFetchHotelPromotions } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import { useCreateAsyncPricing } from '@/app/components/hotel-detail/PricingProvider';
import { useFetchRooms } from '@/app/components/hotel-detail/hooks/useFetchRooms';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { isMealPlanInRooms } from '@/app/components/hotel-detail/rooms/roomCommon';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { URLParameters } from '@/app/common/urlParameters';
import { sendMetricIncrement } from '@/app/components/hotel-detail/metrics';
import { trackEventError, trackVisitEvent } from '@/app/components/hotel-detail/trackVisitEvents';
import { useTranslation } from '@/app/components/translations/Translation';
import type { RoomsDetailInfoPayload } from '@/app/common/starTrack/events/roomsDetailInfo.ts';
import { createRoomsDetailInfoPayload } from '@/app/common/starTrack/events/roomsDetailInfo.ts';
import { createHotelDetailShownPayload } from '@/app/common/starTrack/events/hotelDetailShown.ts';
import { getTrackingVcSearchId } from '@/app/common/tracking/trackingUtils.ts';
import {
  CARD_TYPE,
  resultClickedEvents,
} from '@/app/components/search/components/SearchPageContainer/resultClickedEvents';
import { useFetchSearchProductsForDetailsMap } from '@/app/components/hotel-detail/hooks/useFetchSearchProductsForDetailsMap';
import { updateUrl } from '@/app/common/urlParametersUtils';
import { PAGE_TYPE } from '@/app/common/pageType';
import type { StarTrack } from '@/app/common/starTrack/startrack';
import { createSearchDefaultFlightPayloadByDetails } from '@/app/common/starTrack/events/searchDefaultFlight';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog';
import {
  ampliProductDetailsShownWithoutRooms,
  ampliProductDetailsShownWithRooms,
} from '@/app/common/amplitude/events/ampliProductDetailsShown.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { getUserLoyaltyTier, isLoyaltyUser } from '@/app/common/Loyalty/utils.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';

let globalAlreadySent = false;

function createDiscountLabel(mealPlan: MealPlanType, commonData: CommonData, localization: Localization) {
  const flashSalePromo = mealPlan.promotions?.find((p) => p.type?.toUpperCase() === 'FLASH_SALE');
  const showLastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;

  return mealPlan.isMyPrice
    ? 'myPrice'
    : mealPlan.mobileRate && commonData.isMobileDevice
      ? 'mobileRate'
      : flashSalePromo && showLastMinuteDeals
        ? 'lmnDeal'
        : flashSalePromo
          ? 'flashSales'
          : mealPlan.isEmployeeDiscount
            ? 'employeeDiscount'
            : mealPlan.ratePriceType === 'LOYALTY'
              ? 'loyalty'
              : null;
}

function trackRoomsShown({
  rooms,
  searchId,
  productDetailKey,
  internalHotelId,
  isTopSecret,
  isFlashSale,
  hasLoyaltyRate,
  currency,
  app,
  starTrack,
  commonData,
  localization,
}: {
  rooms: RoomType[] | undefined;
  searchId: string;
  productDetailKey: string;
  internalHotelId: number;
  isTopSecret: boolean;
  isFlashSale: boolean;
  hasLoyaltyRate: boolean;
  currency: string;
  app: boolean;
  starTrack: StarTrack;
  commonData: CommonData;
  localization: Localization;
}) {
  if (!rooms || rooms.length === 0) {
    return;
  }

  const userLoyaltyEnrol = isLoyaltyUser(commonData);
  const loyaltyTier = getUserLoyaltyTier(commonData);
  const loggedIn = commonData?.authResponse?.loggedIn;
  let mealPlanIndex = 0;

  const roomsDetailInfoPayloads: RoomsDetailInfoPayload[] = rooms.flatMap((room, roomIndex) =>
    room.configurations.map((mealPlan) => {
      mealPlanIndex += 1;
      return createRoomsDetailInfoPayload({
        mealPlan,
        room,
        roomIndex,
        optionIndex: mealPlanIndex,
        searchId,
        productDetailKey,
        internalHotelId,
        isTopSecret,
        isFlashSale,
        hasLoyaltyRate,
        currency,
        app,
        localization,
        userLoyaltyEnrol,
        loyaltyTier,
        loggedIn,
        discountLabel: createDiscountLabel(mealPlan, commonData, localization),
      });
    })
  );

  starTrack.roomsDetailInfoMultiple(roomsDetailInfoPayloads).catch((error) => {
    clientAppLog({
      logMessage: `Error sending rooms to track events ROOMS_DETAIL_SHOWN from SSR - productDetailKey: ${productDetailKey} - error: ${error} - payload size: ${JSON.stringify(roomsDetailInfoPayloads).length} bytes`,
      codeSourceName: 'useTrackHotelShown.trackRoomsShown',
      level: 'WARN',
      url: window?.location?.href,
    });
  });
}

export function useTrackHotelShown() {
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { data: hotelPromotions } = useFetchHotelPromotions();
  const { data: asyncPricing } = useCreateAsyncPricing();
  const { data: roomsData } = useFetchRooms();
  const { offersData } = useOffersContext();
  const { selectedMealPlan, selectedRoom } = useSelectedMealPlan();
  const initialParams = useInitialParams();
  const { t } = useTranslation();
  const { staticData, commonData, searchInfoData, searchInfoDetailData, localization, curiosityParams } =
    usePageWrapperData();
  const evolutionEnabled = localization.configValuesApp?.evolutionEnabled;

  const { starTrack } = useCommonClient();
  const { isApp } = commonData;
  const { sendCuriosityShownEvent } = useCuriosity();
  const urlSearchParams = useUrlSearchParams();
  const {
    adults,
    children: numberOfChildren,
    infants,
    mealPlanFilterValues,
    nights,
    totalGuestsWithoutInfants,
    isPastCheckin,
  } = urlSearchParams;
  const searchParams = getSearchParams();
  const { data: searchProducts } = useFetchSearchProductsForDetailsMap();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const koProviderId = searchParams[URLParameters.KOPROVIDER_ID];
  const clickId = searchParams[URLParameters.CLICK_ID];
  const vcSearchId = searchParams[URLParameters.VC_SEARCH_ID];
  const resultViewedOther = searchParams[URLParameters.RESULT_VIEWED_OTHER] === 'true';
  const pageType = searchParams[URLParameters.PAGE_TYPE];
  const fromResults = searchParams[URLParameters.CLICK_ON_CARD] === 'true';

  const trackingCode = `${offersData?.pricingId}-${offersData?.responseCode}`;

  useEffect(() => {
    if (!searchProducts) return;

    if (resultViewedOther) {
      updateUrl({
        queryParamsToRemove: [URLParameters.RESULT_VIEWED_OTHER],
      });
      const dealCard = searchProducts?.dealCards?.find(
        (card) => card.locales?.hotelId === initialParams.internalHotelId
      );
      if (dealCard) {
        resultClickedEvents({
          starTrack,
          cardType: CARD_TYPE.LIST,
          dealCard,
          clickId,
          vcSearchId,
          commonData,
          curiosityParams,
          localization,
        });
      }
    }
  }, [searchProducts]);

  // One-shot per "view" (trackingCode). See the guard inside the tracking effect below.
  const sentTrackingCodeRef = useRef<string | null>(null);

  useEffect(() => {
    // A responseCode is set only on a terminal outcome (OK with rooms, or an error / no-availability /
    // pricing-failure), so it is the true "resolved" signal to gate on. Do NOT also require `roomsData`:
    // hard pricing or rooms *fetch* failures leave roomsData undefined while offersData carries the error
    // responseCode, and the (error-flavoured) HOTEL_DETAIL_SHOWN must still fire instead of being dropped.
    if (!isPastCheckin && !offersData?.responseCode) return;

    // HOTEL_DETAIL_SHOWN and ROOMS_DETAIL_INFO sent from hotel details page
    if (globalAlreadySent && pageType !== PAGE_TYPE.HOTEL_DETAIL) return;

    const hasRooms = offersData?.responseCode === 'OK' && !!offersData?.rooms;

    // Evo reports the PRESELECTED package (SelectedMealPlanProvider), populated in a PARENT effect
    // that runs AFTER this child effect. Wait for a selection that belongs to THESE offers, or the
    // event pairs new dates with the previous package. Legacy keeps using the cheapest package.
    if (hasRooms && evolutionEnabled && !isMealPlanInRooms(offersData?.rooms, selectedMealPlan)) return;

    // One-shot per "view" (trackingCode = pricingId-responseCode). Avoids re-emitting when the user
    // changes room (selectedMealPlan changes with the SAME trackingCode) — on the HDP the
    // globalAlreadySent guard above never early-returns — while still re-emitting on a genuinely new
    // view: a date/pricing change yields a new trackingCode (and the apex-calendar reset clears the
    // selection, so the wait above re-runs and emits with the new preselected package).
    if (sentTrackingCodeRef.current === trackingCode) return;
    sentTrackingCodeRef.current = trackingCode;

    if (offersData?.responseCode === 'OK' && offersData?.rooms) {
      // Package to report: preselected in Evo, cheapest in legacy.
      const packageMealPlan = evolutionEnabled ? selectedMealPlan : offersData.cheapestFilteredMealPlan;
      const packageRoom = evolutionEnabled ? selectedRoom : offersData.rooms[0];
      if (pageType === PAGE_TYPE.HOTEL_DETAIL) {
        sendCuriosityShownEvent({
          people_child: numberOfChildren,
          people_infant: infants,
          checkin_date: offersData.checkin
            ? format(parse(offersData.checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
            : '',
          checkout_date: offersData.checkout
            ? format(parse(offersData.checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
            : '',
          nights: offersData.nights || nights,
          room_quantity: offersData.rooms.length,
          response_code: 'OK',
          cheapest_price: offersData.cheapestFilteredMealPlan?.finalTotalPrice,
          cheapest_price_currency: localization.currency,
          is_flash_sales: hotelPromotions?.isFlashSale ? 1 : 0,
          is_secret_hotel: hotelStaticData?.isTopSecret ? 1 : 0,
          is_app_true: isApp ? 1 : 0,
          cheapest_room_has_photo: offersData.rooms[0].images.length > 0 ? 1 : 0,
          cheapest_room_id: offersData.rooms[0].roomId,
          cheapest_room_provider_id: offersData.rooms[0].configurations[0].supplier.id,
          has_meal_plan_filter: mealPlanFilterValues.join(','),
          rooms_with_photo: offersData.rooms.reduce((acc, room) => (room.images.length > 0 ? acc + 1 : acc), 0),
          has_ko_provider: koProviderId ? 1 : 0,
        });
      }

      sendMetricIncrement(`app.detail-shown.${initialParams.pageType}.total`);
      if (koProviderId) {
        sendMetricIncrement(`app.detail-shown.${initialParams.pageType}.with-koprovider.total`);
      }
      if (fromResults) {
        updateUrl({
          queryParamsToRemove: [URLParameters.CLICK_ON_CARD],
        });
      } else if (offersData?.transport && (offersData.transport.transports?.length ?? 0) > 0) {
        starTrack.searchDefaultFlight(
          createSearchDefaultFlightPayloadByDetails({
            searchId,
            vcSearchId: getTrackingVcSearchId(offersData, vcSearchId),
            transport: offersData.transport.transports[0],
            passeengersWithLuggage: adults + numberOfChildren,
          })
        );
      }
      starTrack.hotelDetailShown(
        createHotelDetailShownPayload({
          hasRooms: true,
          searchId,
          vcSearchId,
          clickId: searchParams[URLParameters.CLICK_ID],
          initialParams,
          pricingId: asyncPricing?.pricingId,
          hotelStaticData,
          offersData,
          localization,
          pageType,
          packageMealPlan,
          packageRoom,
          roomsData,
          totalGuestsWithoutInfants,
        })
      );
      ampliProductDetailsShownWithRooms({
        searchInfoData,
        hotelStaticData,
        initialParams,
        offersData,
        commonData,
        localization,
        packageMealPlan,
      });
      trackRoomsShown({
        rooms: offersData.rooms,
        searchId: searchId || '',
        productDetailKey: initialParams.productDetailKey || '',
        internalHotelId: initialParams.internalHotelId,
        isTopSecret: hotelStaticData?.isTopSecret || false,
        isFlashSale: hotelPromotions?.isFlashSale || false,
        hasLoyaltyRate: !!offersData?.hasLoyaltyOffers,
        currency: localization.currency,
        app: isApp || false,
        starTrack,
        commonData,
        localization,
      });

      if (asyncPricing?.pricingId && pageType === PAGE_TYPE.HOTEL_DETAIL) {
        trackVisitEvent({
          mealPlan: packageMealPlan,
          roomName: packageRoom?.name ?? '',
          checkin: offersData.checkin || '',
          checkout: offersData.checkout || '',
          hotelStaticData,
          initialParams,
          promotionType: hotelPromotions?.promotionType || '',
          urlSearchParams,
          pricingId: asyncPricing?.pricingId,
          pageSection: 'hdp.details',
          staticData,
          commonData,
          localization,
          searchInfoDetailData,
          t,
        });
      }
    } else {
      if (pageType === PAGE_TYPE.HOTEL_DETAIL) {
        sendCuriosityShownEvent({
          response_code: offersData?.responseCode,
        });
      }
      starTrack.hotelDetailShown(
        createHotelDetailShownPayload({
          hasRooms: false,
          searchId,
          vcSearchId,
          clickId: searchParams[URLParameters.CLICK_ID],
          initialParams,
          pricingId: asyncPricing?.pricingId,
          hotelStaticData,
          offersData,
          localization,
          pageType,
          isPastCheckin,
        })
      );
      ampliProductDetailsShownWithoutRooms({
        searchInfoData,
        hotelStaticData,
        initialParams,
        offersData,
        commonData,
        isPastCheckin,
      });
      trackEventError({
        errorType: !offersData?.responseCode && isPastCheckin ? 'PAST_DATES' : 'ERROR_FETCHING_ROOMS',
        initialParams,
        urlSearchParams,
        hotelStaticData,
        hotelPromotions,
        pricingId: asyncPricing?.pricingId || '',
        pageSection: 'hdp.details',
        staticData,
        commonData,
        localization,
        searchInfoDetailData,
      });
    }
    globalAlreadySent = true;
  }, [trackingCode, selectedMealPlan, selectedRoom]);
}
